import React from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils/cn';

export type AdGroupStatus = 'PAUSED' | 'ENABLED';

interface AdGroupBasicsStepProps {
  name: string;
  status: AdGroupStatus;
  onChangeName: (name: string) => void;
  onChangeStatus: (status: AdGroupStatus) => void;
  errors?: {
    name?: string;
  };
}

const AD_GROUP_STATUSES: Array<{ value: AdGroupStatus; label: string; description: string }> = [
  {
    value: 'ENABLED',
    label: 'Enabled',
    description: 'Ad group will start serving ads immediately (once keywords and ads are added)',
  },
  {
    value: 'PAUSED',
    label: 'Paused',
    description: 'Ad group will not serve ads until manually enabled',
  },
];

export const AdGroupBasicsStep: React.FC<AdGroupBasicsStepProps> = ({
  name,
  status,
  onChangeName,
  onChangeStatus,
  errors,
}) => {
  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Ad Group Basics</h3>
        <p className="text-sm text-gray-600">
          Set up the basic details for your new ad group.
        </p>
      </div>

      {/* Ad Group Name */}
      <div>
        <label htmlFor="ad-group-name" className="block text-sm font-medium text-gray-700 mb-2">
          Ad Group Name <span className="text-red-500">*</span>
        </label>
        <Input
          id="ad-group-name"
          type="text"
          value={name}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="e.g., Running Shoes"
          maxLength={255}
          className={cn({
            'border-red-500 focus:border-red-500 focus:ring-red-500': errors?.name,
          })}
          aria-invalid={!!errors?.name}
          aria-describedby={errors?.name ? 'ad-group-name-error' : undefined}
        />
        {errors?.name && (
          <p id="ad-group-name-error" className="mt-1 text-sm text-red-600">
            {errors.name}
          </p>
        )}
        <p className="mt-1 text-xs text-gray-500">
          Choose a descriptive name for this ad group. {name.length}/255 characters.
        </p>
      </div>

      {/* Ad Group Status */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Initial Status <span className="text-red-500">*</span>
        </label>
        <div className="space-y-2">
          {AD_GROUP_STATUSES.map((statusOption) => {
            const isSelected = status === statusOption.value;
            return (
              <label
                key={statusOption.value}
                className={cn(
                  'flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all',
                  'hover:bg-gray-50 focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
                  {
                    'border-blue-600 bg-blue-50': isSelected,
                    'border-gray-200': !isSelected,
                  }
                )}
              >
                <input
                  type="radio"
                  name="ad-group-status"
                  value={statusOption.value}
                  checked={isSelected}
                  onChange={() => onChangeStatus(statusOption.value)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-0.5"
                  aria-describedby={`status-${statusOption.value}-description`}
                />
                <div className="ml-3 flex-1">
                  <span className="block text-sm font-medium text-gray-900">
                    {statusOption.label}
                  </span>
                  <p
                    id={`status-${statusOption.value}-description`}
                    className="text-xs text-gray-500 mt-1"
                  >
                    {statusOption.description}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};
